import type { DiskUsageScope } from "@/services/resourceUsageService"

/**
 * Storage cost in USD per GB per month for each scope.
 *
 * The `userdata` scope is cheaper because infrequently accessed data is
 * automatically moved to a lower-cost storage tier after 30 days. The
 * Posit Homes scopes use EFS which is billed at a flat rate.
 */
export const COST_PER_GB: Record<DiskUsageScope, number> = {
  userdata: 0.088,
  wa_posit_homes: 0.36,
  ea_posit_homes: 0.36,
}

/**
 * Formats a byte count into a human-readable string with an appropriate unit.
 *
 * Examples: 0 → "0 B", 1536 → "1.50 KB", 1073741824 → "1.00 GB".
 *
 * @param bytes - The number of bytes to format. Must be >= 0.
 * @returns A formatted string such as "1.50 GB".
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B"
  const units = ["B", "KB", "MB", "GB", "TB"]
  // Determine the highest unit where the value is >= 1
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  return `${value.toFixed(2)} ${units[exponent]}`
}

/**
 * Formats a dollar amount as a human-readable cost string.
 *
 * Returns "$0.00" for zero, "< $0.01" for sub-cent values, or "$X.XX" otherwise.
 *
 * @param cost - The cost in USD. Must be >= 0.
 * @returns A formatted string such as "$1.23" or "< $0.01".
 */
export function formatCost(cost: number): string {
  if (cost === 0) return "$0.00"
  if (cost < 0.01) return "< $0.01"
  return `$${cost.toFixed(2)}`
}

/**
 * Formats a version timestamp string (YYYYMMDDHHmm) into a human-readable
 * date and time string using the en-GB locale.
 *
 * Example: "202504101430" → "10 Apr 2025, 14:30".
 *
 * @param version - The snapshot version string, expected to be exactly 12 characters.
 * @returns A formatted date/time string, or the original string if it cannot be parsed.
 */
export function formatSnapshotDate(version: string): string {
  if (version.length !== 12) return version
  const year = version.slice(0, 4)
  const month = version.slice(4, 6)
  const day = version.slice(6, 8)
  const hour = version.slice(8, 10)
  const minute = version.slice(10, 12)
  const date = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hour),
    parseInt(minute)
  )
  if (isNaN(date.getTime())) return version
  return (
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })
  )
}

/**
 * Estimates the monthly storage cost for a given number of bytes in a scope.
 *
 * Formula: (bytes / 1 GiB) * COST_PER_GB[scope]
 *
 * @param bytes - The size in bytes.
 * @param scope - The storage scope that determines the per-GB rate.
 * @returns The estimated cost in USD per month.
 */
export function estimateMonthlyCost(bytes: number, scope: DiskUsageScope): number {
  const gb = bytes / (1024 * 1024 * 1024)
  return gb * COST_PER_GB[scope]
}

/**
 * Estimates the weekly storage cost for a given number of bytes in a scope.
 *
 * Derived from the monthly cost by multiplying by 12 months and dividing by
 * 52 weeks (i.e., monthly * 12 / 52).
 *
 * @param bytes - The size in bytes.
 * @param scope - The storage scope that determines the per-GB rate.
 * @returns The estimated cost in USD per week.
 */
export function estimateWeeklyCost(bytes: number, scope: DiskUsageScope): number {
  return (estimateMonthlyCost(bytes, scope) * 12) / 52
}
